// components/dashboard/SystemStatus.jsx
// Area 3 — "The system is working": a calm confidence strip.
//  - Activity: emails sent (period) + how many are scheduled to go out.
//  - Honest engagement: sent · CLICKED (reliable) · attributed orders.
//    Open rate is deliberately NOT a headline metric — Apple MPP / Gmail proxies
//    inflate it. Clicks and attributed purchases are the trustworthy signals.
//  - Revenue by email type: the attributed revenue (refund-aware, #68/#74) split
//    by message type — "where your revenue comes from". Real numbers only.

import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, MousePointerClick } from "lucide-react";
import { safeApi, isFeatureError } from "@/utils/api";

const TYPE_LABELS = {
  thank_you: "Thank you",
  review_request: "Review request",
  vip_upsell: "VIP upsell",
  custom_offer: "Custom offer",
  win_back: "Win back",
};
const prettyType = (t) => TYPE_LABELS[t] || String(t || "other").replace(/_/g, " ");

export default function SystemStatus({ period = "30d", stats = {} }) {
  const [attribution, setAttribution] = useState(null);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await safeApi.get(`/attribution?period=${period}`);
        if (!active) return;
        if (isFeatureError(res)) setGated(true);
        else {
          setGated(false);
          setAttribution(res?.data || res);
        }
      } catch (e) {
        /* leave prior data; engagement still renders from stats */
      }
    })();
    return () => {
      active = false;
    };
  }, [period]);

  const sent = Number(stats.messages_sent || 0);
  const scheduled = Number(stats.scheduled_messages || 0);
  // Honest header: "working" is a claim we only earn once something has
  // actually been sent or is queued to go out. With zero activity (a brand-new
  // shop, or one with no templates so nothing can fire) the system is "ready",
  // not "working" — never claim motion that isn't happening.
  const hasActivity = sent > 0 || scheduled > 0;
  const clicked = Number(stats.messages_clicked || 0);
  const clickRate = Number(stats.click_rate || 0);
  const attributedOrders = Number(attribution?.summary?.total_attributed_orders || 0);

  const byType = attribution?.by_message_type || {};
  const revenueRows = Object.entries(byType)
    .map(([type, d]) => ({ type, revenue: Number(d.attributed_revenue || 0) }))
    .filter((r) => r.revenue > 0)
    .sort((a, b) => b.revenue - a.revenue);
  const maxRevenue = Math.max(1, ...revenueRows.map((r) => r.revenue));

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="text-sm font-medium text-gray-500 mb-4">
          {hasActivity ? "Your system is working" : "Your system is ready"}
        </h3>

        {/* Activity + honest engagement. "Emails sent" and "Attributed orders" are
            on the revenue card just above — once on the page, not twice. */}
        <div className="grid grid-cols-2 gap-4">
          <Stat icon={Clock} tint="text-amber-600 bg-amber-100" value={scheduled.toLocaleString()} label="Scheduled to go out" />
          <Stat
            icon={MousePointerClick}
            tint="text-blue-600 bg-blue-100"
            value={clicked.toLocaleString()}
            label={`Clicked · ${clickRate}%`}
          />
        </div>

        {/* Revenue by email type */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <p className="text-sm font-medium text-gray-700">Revenue by email type</p>
          <p className="text-xs text-gray-400 mb-3">Attributed revenue, by email type</p>

          {gated ? (
            <p className="text-sm text-gray-400">Available on Basic &amp; Pro plans.</p>
          ) : revenueRows.length === 0 ? (
            <p className="text-sm text-gray-400">
              This fills in as customers purchase within the attribution window after your emails —
              real orders only.
            </p>
          ) : (
            <div className="space-y-2.5">
              {revenueRows.map((r) => (
                <div key={r.type} className="flex items-center gap-3">
                  <span className="w-28 shrink-0 text-sm text-gray-600 capitalize truncate">
                    {prettyType(r.type)}
                  </span>
                  {/* minWidth keeps any non-zero revenue visible, even a tiny fraction of the max */}
                  <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500"
                      style={{ width: `${(r.revenue / maxRevenue) * 100}%`, minWidth: "0.5rem" }}
                    />
                  </div>
                  <span className="w-16 shrink-0 text-right text-sm font-semibold text-gray-900">
                    ${r.revenue.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ icon: Icon, tint, value, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${tint}`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <p className="text-lg font-semibold text-gray-900 leading-tight">{value}</p>
        <p className="text-xs text-gray-500 leading-tight truncate">{label}</p>
      </div>
    </div>
  );
}
